// Legal texts for the /terms and /privacy pages — plain markdown strings,
// rendered by LegalPage.jsx through the same safe parser the chat uses.
//
// Source: ToS & Privacy draft v0.9 with all placeholders resolved
// (operator: NavarchAI, contact: mandelosthanasis2@gmail.com, governing law:
// Greece / courts of Piraeus, log retention: 12 months). The draft's
// internal annotations (DRAFT header, lawyer notes) are deliberately NOT
// shown to users — the pages carry a discreet "Beta version" tag instead.

export const LEGAL_LAST_UPDATED = 'July 2026';
export const LEGAL_CONTACT_EMAIL = 'mandelosthanasis2@gmail.com';

export const TERMS_MD = `
## 1. What NavarchAI is

NavarchAI ("the Service") is an AI-powered maritime assistant. It helps seafarers troubleshoot machinery, prepare for inspections, and answer maritime questions, using artificial intelligence and, where applicable, technical documents that you upload to your own private account.

The Service is operated by: NavarchAI. Contact: ${LEGAL_CONTACT_EMAIL}.

By creating an account or using the Service, you agree to these Terms.

## 2. IMPORTANT — Not a substitute for official procedures

The Service provides AI-generated assistance only. It is not a classification society, not an equipment manufacturer, not a surveyor, and not a substitute for official technical documentation, maker's instructions, company procedures (SMS), or the professional judgment of qualified personnel.

- **AI answers may be incomplete, outdated, or wrong.**
- You must always verify any information against official documentation and your company's procedures before acting on it.
- You remain fully responsible for all decisions and actions taken on board. Never rely on the Service for decisions affecting the safety of life, the vessel, cargo, or the environment without independent verification.

## 3. Accounts and eligibility

- You must provide a valid email address and verify it. Keep your credentials confidential; you are responsible for activity under your account.
- The Service is intended for maritime professionals aged 18 or over.
- One free trial per person. Creating multiple accounts to obtain additional free usage is prohibited and may lead to suspension.

## 4. Plans, trial, and fair use

- New accounts receive a limited free trial (a fixed number of messages). Paid plans provide monthly message quotas and manual-upload slots as described on the Plans page.
- During the beta period, plans may be granted manually and free of charge; features, quotas, and prices may change before commercial launch.
- We may apply fair-use limits and rate limits to protect the Service. Quotas reset monthly and unused messages do not roll over.

## 5. Your uploads (Bring Your Own Manuals)

- You may upload technical documents ("User Content") to your private library. You represent and warrant that you have the legal right to upload and use each document you upload (for example under your or your employer's licence from the manufacturer).
- Your uploaded documents are processed into searchable text fragments and stored privately for your account only. They are never added to any shared knowledge base, never shown to other users, and never used to answer other users' questions.
- We do not claim ownership of your User Content. You can delete an uploaded document at any time, which removes its indexed content from the Service.
- We may remove User Content and/or suspend accounts if we receive a credible infringement complaint, and we will comply with applicable notice-and-takedown obligations.
- You must not upload documents that are classified, export-controlled, or that you are contractually prohibited from copying to third-party services.

## 6. Community contributions

- The Service lets you submit short notes based on your own knowledge and experience ("Contributions"). By submitting, you confirm the Contribution is your own knowledge and not copied from copyrighted material.
- If we approve a Contribution, you grant us a worldwide, perpetual, royalty-free licence to use, edit, reproduce, and incorporate it into the Service's shared knowledge base, in original or modified form, without attribution. You keep ownership of your Contribution.
- We review Contributions before they enter the shared knowledge base and may edit or reject them at our discretion.

## 7. Acceptable use

You must not: (a) use the Service for unlawful purposes; (b) attempt to breach, probe, or overload the Service or access other users' data; (c) upload malicious files; (d) resell or provide the Service to third parties without our agreement; (e) use automated means to extract the knowledge base; (f) misrepresent AI output as official manufacturer or class guidance.

## 8. Intellectual property

The Service, its software, design, and the shared knowledge base (excluding your User Content) are owned by us or our licensors. AI-generated answers are provided for your professional use; you may use them in your work on board.

## 9. Availability, beta status, and changes

The Service is provided on an "as available" basis and is currently in beta. We do not guarantee uninterrupted availability (satellite connectivity, third-party AI providers, and maintenance may all cause interruptions). We may modify, suspend, or discontinue features at any time. We will make reasonable efforts to announce material changes.

## 10. Disclaimer of warranties and limitation of liability

To the maximum extent permitted by law:

- The Service is provided "as is" and "as was", without warranties of any kind, express or implied, including accuracy, fitness for a particular purpose, or non-infringement.
- We are not liable for any indirect, incidental, special, or consequential damages, or for loss of profits, hire, cargo, data, or business, arising from use of or inability to use the Service, including reliance on AI-generated answers.
- Our total aggregate liability for any claim shall not exceed the amounts you paid us in the twelve (12) months preceding the claim (or EUR 50 if you paid nothing).
- Nothing in these Terms excludes liability that cannot be excluded by law.

## 11. Indemnity

You will indemnify us against claims arising from (a) your breach of Section 5 (upload rights) or Section 6 (contribution originality), or (b) your unlawful use of the Service.

## 12. Termination

You may stop using the Service and delete your account at any time. We may suspend or terminate accounts that breach these Terms. Sections 5–6, 8, 10–11 survive termination.

## 13. Governing law and disputes

These Terms are governed by the laws of Greece, and disputes are subject to the exclusive jurisdiction of the courts of Piraeus.

## 14. Changes to these Terms

We may update these Terms. We will notify registered users of material changes by email or in-app notice. Continued use after the effective date constitutes acceptance.
`.trim();

export const PRIVACY_MD = `
## 1. Who we are

Data controller: NavarchAI. Contact: ${LEGAL_CONTACT_EMAIL}.

## 2. What we collect

- **Account data:** email address, hashed password (managed by our authentication provider), plan and admin status.
- **Usage data:** your questions to the AI, the equipment/module they concern, the AI model used, token counts and processing cost, timestamps, feedback you give (ratings and comments), and contributions you submit.
- **Uploaded documents:** the text content of manuals you upload, stored as private indexed fragments linked to your account. The original PDF file itself is not stored on our servers — processing happens in your browser.
- **Technical data:** standard server logs (IP address, user agent) kept by our hosting providers for security and abuse prevention.
- We do not intentionally collect special-category (sensitive) personal data. Please do not include personal data of others in your questions or notes.

## 3. Why we process it (legal bases)

- **To provide the Service (contract):** accounts, quotas, answering your questions, storing your library.
- **To improve the Service (legitimate interest):** analysing questions and feedback to fix weak answers and to decide what content to add to the shared knowledge base. Your questions are logged and linked to your account for this purpose.
- **To protect the Service (legitimate interest):** rate limiting, abuse and fraud prevention, security logging.
- Legal obligations, where applicable.

## 4. Processors and transfers

We use trusted processors to operate the Service:

- **Supabase** — database and authentication (account data, indexed content, logs).
- **Vercel** — application hosting and serverless functions.
- **Anthropic** — AI answers: your questions and relevant text fragments are sent to Anthropic's API to generate responses.
- **OpenAI** — text embeddings: fragments of your documents and your questions are sent to OpenAI's API to create numerical search indexes.
- **DeepSeek** — backup AI provider: if activated, questions and relevant text fragments are processed by DeepSeek's API to generate responses.

These providers may process data outside the EEA; transfers rely on their standard contractual clauses / adequacy mechanisms. We do not sell personal data and do not use it for third-party advertising.

## 5. Retention

- Account data: for as long as your account exists.
- Uploaded document fragments: until you delete the document or your account.
- Question logs and feedback: retained while your account exists, then deleted or anonymised within 12 months. Aggregated/anonymised statistics may be kept indefinitely.

## 6. Your rights

Subject to applicable law (including GDPR), you may request access, correction, deletion, restriction, portability, or object to processing based on legitimate interests. Contact ${LEGAL_CONTACT_EMAIL}. You may also complain to your data protection authority. Deleting your account removes your private library and personal data, except records we must keep by law.

## 7. Security

Access to data is restricted (row-level security per account; administrative access limited to the operator). Communications are encrypted in transit. No system is perfectly secure; notify us immediately of any suspected breach of your account.

## 8. Changes

We will notify registered users of material changes to this Policy.
`.trim();
